import moment from 'moment-timezone';

import { DateSelectArg, DatesSetArg, EventApi, EventClickArg, EventInput, ViewApi } from '@fullcalendar/common';
import { Calendar as FullCalendar, EventDropArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { EventResizeDoneArg } from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import timeGridPlugin from '@fullcalendar/timegrid';

import { initCalendarNotice } from './calendar-notice';
import { initFormFields, onFormSubmit } from './components/Form';
import Tabs from './components/Tabs';
import Event, { IEventData } from './models/Event';
import { weekStartDay } from './week-start';

import { ModalEventHandler } from 'bootstrap';
import './../sass/calendar.scss';

interface IDetailSettings {
    details: string;
    duration?: number;
    endDate?: string;
}

interface IEventPublishResponse {
    modal?: string;
    events: IEventData | IEventData[];
}

type EventType = 'paid' | 'free_registered' | 'free_public';

class Calendar {
    readonly element: JQuery;
    readonly calendar: FullCalendar;
    readonly calendarElement: JQuery;
    readonly tabs: Tabs;

    private modal: JQuery;
    private roomId: number;
    private isSelecting: boolean = false;
    private selectedEvents: EventApi[] = [];
    private selectedEventIds: string[] = [];

    constructor() {
        this.element = $('.js-calendar');
        this.tabs = new Tabs(this.element.find('.calendar__tabs'));
        this.calendarElement = this.element.find('.calendar__element');
        if (this.calendarElement.length) {
            // Init tabs for calendar
            this.tabs.onTabChange = this.onTabChange.bind(this);
            // Add calendar instance
            this.calendar = new FullCalendar(this.calendarElement.get(0), {
                rerenderDelay: 50,
                allDaySlot: false,
                timeZone: window.app.timeZone,
                datesSet: this.datesRender.bind(this),
                initialView: 'timeGridWeek',
                droppable: true,
                editable: false,
                expandRows: true,
                eventClick: this.eventClick.bind(this),
                eventDrop: this.eventUpdate.bind(this),
                eventDurationEditable: true,
                eventOverlap: false,
                eventResize: this.eventUpdate.bind(this),
                firstDay: weekStartDay(),
                height: 'auto',
                locale: window.app.locale,
                select: this.onSelect.bind(this),
                selectable: true,
                slotDuration: '00:30',
                slotMaxTime:
                    window.app.calendar && typeof window.app.calendar.max_time !== 'undefined'
                        ? window.app.calendar.max_time
                        : '24:00:00',
                slotMinTime:
                    window.app.calendar && typeof window.app.calendar.min_time !== 'undefined'
                        ? window.app.calendar.min_time
                        : '00:00:00',
                snapDuration: '00:15',
                titleFormat: {
                    day: '2-digit',
                    month: '2-digit',
                    weekday: 'long',
                },
                slotLabelFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    omitZeroMinute: false,
                },
                eventTimeFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                    omitZeroMinute: false,
                },
                views: {
                    timeGridWeek: {
                        dayHeaderFormat: {
                            month: '2-digit',
                            day: '2-digit',
                            weekday: 'short',
                        },
                    },
                },
                buttonText: {
                    today: window.app.strings.today,
                    day: window.app.strings.day,
                    month: window.app.strings.month,
                    week: window.app.strings.week,
                },
                plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, momentTimezonePlugin],
                customButtons: {
                    unlock: {
                        text: window.app.strings.unlock,
                        click: (): void => {
                            this.calendar.setOption('editable', true);
                            this.calendar.setOption('headerToolbar', {
                                right:
                                    (this.isSelecting ? 'publish' : 'select') +
                                    ' lock timeGridDay,timeGridWeek,dayGridMonth',
                            });
                        },
                    },
                    lock: {
                        text: window.app.strings.lock,
                        click: (): void => {
                            this.calendar.setOption('editable', false);
                            this.calendar.setOption('headerToolbar', {
                                right: 'unlock timeGridDay,timeGridWeek,dayGridMonth',
                            });
                        },
                    },
                    select: {
                        text: window.app.strings.start_bulk_publish,
                        click: (): void => {
                            this.isSelecting = true;
                            this.calendar.setOption('headerToolbar', {
                                right:
                                    'publish ' +
                                    (this.calendar.getOption('editable') ? 'lock' : 'unlock') +
                                    ' timeGridDay,timeGridWeek,dayGridMonth',
                            });
                        },
                    },
                    publish: {
                        text: window.app.strings.publish,
                        click: this.publishEvents.bind(this),
                    },
                },
                headerToolbar: {
                    left: 'prev,today,next',
                    center: 'title',
                    right: 'unlock timeGridDay,timeGridWeek,dayGridMonth',
                },
            });
        }
    }

    public init(): void {
        this.roomId = this.tabs.currentTab.data('room-id');
        if (this.calendar) {
            this.calendar.render();
        }
        initCalendarNotice();
    }

    public onTabChange = (tab: JQuery<HTMLElement>): void => {
        this.roomId = tab.data('room-id');
        this.updateEvents(this.calendar.view);
    };

    public updateEvents(view: ViewApi): void {
        $.ajax({
            data: {
                endDate: moment(view.currentEnd).format(),
                roomId: this.roomId,
                startDate: moment(view.currentStart).format(),
            },
            dataType: 'json',
            processData: true,
            type: 'GET',
            url: '/api/calendar/events',
        }).then((events: IEventData[]) => {
            // Reset events list
            this.calendar.removeAllEvents();

            // Add new events to calendar
            events.forEach(this.addOrUpdateEvent);
        });
    }

    public datesRender = (info: DatesSetArg): void => {
        this.updateEvents(info.view);
    };

    public eventClick = (info: EventClickArg): void => {
        if (this.isSelecting) {
            const selectedClass: string = 'is-selected';
            const element: JQuery = $(info.el);
            if (element.hasClass(selectedClass)) {
                element.removeClass(selectedClass);
                this.selectedEvents = this.selectedEvents.filter((event: EventApi) => event.id !== info.event.id);
            } else {
                element.addClass(selectedClass);
                this.selectedEvents.push(info.event);
            }
        } else {
            $.ajax({
                type: 'GET',
                url: '/api/events/modal/' + info.event.id,
            }).then((html: string): void => {
                this.modal = $(html);
                // Drop any previously-loaded event modals (including ones still lingering during
                // Bootstrap's close animation) so the static field ids they contain
                // (e.g. option-status-draft/-published) stay unique. Duplicate ids make
                // <label for> resolve to the wrong modal, breaking the Status radio selection.
                this.removeEventModals();
                this.calendarElement.after(this.modal);

                initFormFields(this.modal);
                this.modal.modal('show');
            });
        }
    };

    public onSelect = (info: DateSelectArg): void => {
        $.ajax({
            type: 'GET',
            url: '/api/events/modal',
        }).then((html: string): void => {
            this.modal = $(html);
            // See removeEventModals(): keep a single event modal in the DOM so its static field
            // ids stay unique and <label for> radio toggling (Status, Visibility) works reliably.
            this.removeEventModals();
            this.calendarElement.after(this.modal);

            // Init form fields
            initFormFields(this.modal);

            // Update date values
            this.modal.find('[name="start_date"]').data('DateTimePicker').date(moment(info.startStr));
            this.modal.find('[name="end_date"]').val('');
            this.checkTicketSystem();

            // Show modal
            this.modal.modal('show');
        });
    };

    public eventUpdate = (info: EventResizeDoneArg | EventDropArg): void => {
        $.ajax({
            data: {
                start_date: moment(info.event.start).format(),
                end_date: info.event.end ? moment(info.event.end).format() : null,
            },
            type: 'PUT',
            url: '/api/events/' + info.event.id,
        })
            .then(this.addOrUpdateEvent)
            .fail(info.revert);
    };

    public addSessions = (): void => {
        const startDate: JQuery = this.modal.find('[name="start_date"]');
        const endDate: JQuery = this.modal.find('[name="end_date"]');
        const name: JQuery = this.modal.find('[name="name"]');
        if (this.modal.find('select[name="production_id"]').val() && startDate.val() && endDate.val()) {
            const sessionsContainer: JQuery = this.modal.find('[data-component="duplicate-sessions"]');
            const sessionId: string = `${startDate.val()}-${endDate.val()}`;
            sessionsContainer.removeClass('hidden');
            if (!sessionsContainer.find(`[data-id="${sessionId}"]`).length) {
                const sessionList: JQuery = sessionsContainer.find('.duplicate-sessions');
                const sessionName: string = `${startDate.val()} - ${endDate.val()}${name.val() ? ' | ' + name.val() : ''}`;
                const sessionIndex: number = sessionList.find('.duplicate-sessions__item').length;
                const sessionInputs: string[] = [
                    `<input type="hidden" name="duplicate_sessions[${sessionIndex}][name]" value="${name.val()}">`,
                    `<input type="hidden" name="duplicate_sessions[${sessionIndex}][start_date]" value="${startDate.val()}">`,
                    `<input type="hidden" name="duplicate_sessions[${sessionIndex}][end_date]" value="${endDate.val()}">`,
                ];
                const sessionHtml: string = `<div class="duplicate-sessions__item" data-id="${sessionId}"><span class="duplicate-sessions__times">&times;</span>${sessionName}${sessionInputs.join('')}</div>`;
                sessionList.append(sessionHtml);
            }
        }
    };

    public hasDuplicates = (): void => {
        const duplicates: JQuery = this.modal.find('.duplicate-sessions__item').parent();
        const duplicateContainer: JQuery = this.modal.find('.duplicate-sessions');
        if (duplicates.children().length === 0) {
            duplicateContainer.parent().addClass('hidden');
        }
    };

    public getProductionDetails = (productionId: string | string[] | number): void => {
        if (productionId) {
            $.ajax({
                type: 'GET',
                dataType: 'json',
                url: '/api/production/' + productionId,
                data: {
                    startDate: this.modal.find('[name="start_date"]').val(),
                },
            }).then((response: { success: string; data: IDetailSettings }): void => {
                if (response.success) {
                    const details: JQuery = this.modal.find('[data-component="production-details"]');
                    details.html(response.data.details);
                    if (response.data.endDate) {
                        const endDate: JQuery = this.modal.find('[name="end_date"]');
                        endDate.val(response.data.endDate);
                    }

                    if (response.data.duration) {
                        const duration: JQuery = this.modal.find('[name="production_duration"]');
                        duration.val(response.data.duration);
                    }

                    calendar.checkTicketSystem();
                    calendar.updateEndTime();
                    calendar.toggleAutoPublishNotification();
                    calendar.handleEventTypeChange();
                }
            });
        }
    };

    public closeModal = (): void => {
        if (this.modal) {
            this.modal.modal('hide');
        }
    };

    /**
     * Remove every event modal currently in the DOM.
     *
     * Event modals are injected as siblings of the calendar and only removed on `hidden.bs.modal`,
     * which fires after Bootstrap's close animation. Calling this before inserting a new modal
     * guarantees a single event modal at a time, so the static field ids inside them
     * (option-status-draft/-published, option-public-*) are never duplicated.
     */
    public removeEventModals = (): void => {
        this.element.find('.modal[id^="event_"]').remove();
    };

    public publishEvents = (): void => {
        this.isSelecting = false;
        this.calendar.setOption('headerToolbar', {
            right:
                'select ' +
                (this.calendar.getOption('editable') ? 'lock' : 'unlock') +
                ' timeGridDay,timeGridWeek,dayGridMonth',
        });
        this.selectedEventIds = this.selectedEvents.map((event: EventApi) => event.id);
        this.selectedEvents = [];
        if (!this.selectedEventIds.length) {
            return;
        }

        const ids: string[] = this.selectedEventIds;

        $.ajax({
            data: {
                ids,
            },
            type: 'POST',
            url: '/api/events/publish',
        }).then(this.handlePublishResponse);
    };

    public handlePublishResponse = (response: IEventPublishResponse): void => {
        if (!response.modal) {
            this.addOrUpdateEvent(response.events);
            this.success();

            return;
        }

        this.modal = $(response.modal);
        const modal: JQuery = this.element.find('#confirm_publish_events');
        if (!modal.length) {
            this.calendarElement.after(this.modal);
        } else {
            modal.replaceWith(this.modal);
        }

        this.modal.modal('show');
    };

    public confirmPublishEvents = (): void => {
        const ids: string[] = this.selectedEventIds;

        $.ajax({
            data: {
                ids,
            },
            type: 'POST',
            url: '/api/events/publish/force',
        })
            .then(this.addOrUpdateEvent)
            .then(this.closeModal)
            .then(this.success);
    };

    public cancelConfirmPublishEvents = (): void => {
        this.selectedEventIds = [];
        this.element.find('.calendar__event').removeClass('is-selected');
        this.closeModal();
    };

    public addOrUpdateEvent = (eventsData: IEventData | IEventData[]): void => {
        const events: IEventData[] = Array.isArray(eventsData) ? eventsData : [eventsData];
        events.forEach((data: IEventData) => {
            const eventApi: EventApi | null = this.calendar.getEventById('' + data.id);
            if (eventApi) {
                eventApi.remove();
            }

            const event: Event = new Event(data);
            const eventInput: EventInput = {
                id: event.id,
                title: event.name + (!event.public ? ' (private)' : ''),
                start: event.startDate.toDate(),
                end: event.endDate.toDate(),
                backgroundColor: event.color,
                borderColor: event.color,
                durationEditable: false,
                classNames: ['calendar__event', `calendar__event--${event.status}`],
            };

            this.calendar.addEvent(eventInput);
        });
    };

    public checkTicketSystem(): void {
        const input: JQuery = $('.form__update-event [name="ticket_system"]:checked');
        const value: string = '' + input.val();
        const salesUrlHolder: JQuery = input.parents('form').find('[name="ticket_sales_url"]').parents('.form-group');

        switch (value) {
            case 'internal':
                salesUrlHolder.addClass('hidden');
                break;

            case 'external':
                salesUrlHolder.removeClass('hidden');
                break;
        }
    }

    /**
     * Handle visibility dropdown change
     */
    public handleVisibilityChange(): void {
        const modal = $('.modal:visible');
        const visibilitySelect = modal.find('select[name="visibility"]');

        if (!visibilitySelect.length) {
            return;
        }

        const visibility = visibilitySelect.val() as string;
        const linkContainer = modal.find('.screening-link-container');

        // Show/hide the screening link container based on visibility selection
        if (visibility === 'hidden_link') {
            linkContainer.slideDown(200);
        } else {
            linkContainer.slideUp(200);
        }
    }

    public handleEventTypeChange(): void {
        const modal = $('.modal:visible');
        const eventTypeSelect = modal.find('select[name="event_type"]');

        if (!eventTypeSelect.length) {
            return;
        }

        const eventTypeValue = eventTypeSelect.val() as string;
        const validTypes: EventType[] = ['paid', 'free_registered', 'free_public'];

        // Validate event type
        if (!validTypes.includes(eventTypeValue as EventType)) {
            console.error('Invalid event type:', eventTypeValue);
            return;
        }

        const eventType = eventTypeValue as EventType;

        // Find ticket group field
        const ticketGroupSelect = modal.find('select[name="ticket_group_id"]');
        const ticketGroupField = ticketGroupSelect.closest('.form-group');

        // Remove any existing feedback
        modal.find('.event-type-feedback').remove();

        // Check if changing from free to paid
        const previousEventType = modal.data('previousEventType');
        if (eventType === 'paid' && (previousEventType === 'free_public' || previousEventType === 'free_registered')) {
            // Clear the ticket group selection when changing from free to paid
            // This ensures user must select a new ticket group for paid events
            ticketGroupSelect.val('').trigger('change');
        }

        // Store current event type for next change
        modal.data('previousEventType', eventType);

        if (eventType === 'free_public' || eventType === 'free_registered') {
            // Remove required attribute to prevent validation issues
            ticketGroupSelect.prop('required', false);
            ticketGroupSelect.removeAttr('required');

            // Get translation for the helper text
            const currentLang = document.documentElement.lang || 'et';
            const translations: Record<string, string> = {
                en: 'Automatically assigned (free tickets)',
                et: 'Määratakse automaatselt (tasuta piletid)',
                fi: 'Määritetään automaattisesti (ilmaiset liput)',
                lt: 'Priskiriama automatiškai (nemokami bilietai)',
                lv: 'Piešķirts automātiski (bezmaksas biļetes)',
                'de-AT': 'Automatisch zugewiesen (kostenlose Tickets)',
            };
            const feedbackText = translations[currentLang] || translations.en;

            // Hide only the select2 container, not the entire form group
            const select2Container = ticketGroupField.find('.select2-container');
            if (select2Container.length) {
                select2Container.hide();
            }

            // Also hide the original select element
            ticketGroupSelect.hide();

            // Create a placeholder element that maintains the same visual space
            const placeholderHtml = `
                <div class="event-type-feedback form-control" style="
                    background-color: #f5f5f5;
                    border: 1px solid #d2d6de;
                    color: #666;
                    cursor: not-allowed;
                    padding: 6px 12px;
                    font-size: 14px;
                    line-height: 1.42857143;
                    border-radius: 3px;
                ">
                    ${feedbackText}
                </div>
            `;

            // Insert the placeholder after the select (or select2 container)
            if (select2Container.length) {
                select2Container.after(placeholderHtml);
            } else {
                ticketGroupSelect.after(placeholderHtml);
            }
        } else {
            // Restore required attribute
            ticketGroupSelect.prop('required', true);
            ticketGroupSelect.attr('required', 'required');

            // Show the select element
            ticketGroupSelect.show();

            // Show the select2 container if it exists
            const select2Container = ticketGroupField.find('.select2-container');
            if (select2Container.length) {
                select2Container.show();
            }
        }
    }

    public updateEndTime(): void {
        const startDateFieldValue: string = this.modal.find('input[name="start_date"]').val() as string;

        this.modal.find('.calendar__end-time').removeClass('hidden');

        if (startDateFieldValue.length > 0) {
            const bufferTime: number = parseInt(this.modal.find('[name="buffer_time"]').val() as string);
            const startDateTime: string[] = startDateFieldValue.split(' ');
            const formattedStartDate: string = `${startDateTime[0].split('.').reverse().join('-')} ${startDateTime[1]}`;
            const endDateField: JQuery = this.modal.find('input[name="end_date"]');
            const productionDuration: number = parseInt(
                this.modal.find('input[name="production_duration"]').val() as string
            );
            const endTimeText: JQuery = this.modal.find('.cashier__end-time');
            const endDate: moment.Moment = moment(formattedStartDate).add(productionDuration + bufferTime, 'minutes');

            endTimeText.text(endDate.format('HH:mm'));
            endDateField.val(endDate.format('DD.MM.YYYY HH:mm'));
        }
    }

    public removeEvent(eventId: number, confirmMessage: string, url: string): void {
        if (confirm(confirmMessage)) {
            $.post(url, (data: object) => {
                // @ts-ignore
                if (data.success) {
                    const eventApi: EventApi | null = this.calendar.getEventById('' + eventId);
                    if (eventApi) {
                        eventApi.remove();
                    }
                    // @ts-ignore
                    this.success(data.message);
                    $('#event_' + eventId).modal('hide');
                } else {
                    // @ts-ignore
                    this.error(data.message);
                }
            });
        }
    }

    public toggleAutoPublishNotification(): void {
        if (!this.modal) {
            return;
        }

        if (
            this.modal.find('[name="status"]:checked').val() === 'published' &&
            this.modal.find('[name="public"]:checked').val() === '1' &&
            this.modal.find('.js-production-details').data('visibility') !== 'public'
        ) {
            this.modal.find('.js-event-auto-publish-notification').show();
        } else {
            this.modal.find('.js-event-auto-publish-notification').hide();
        }
    }

    public success(message: void | string = ''): void {
        const successMessage = message ? message : 'test';

        // @ts-ignore
        window.toastr.success(successMessage);
    }

    public error(message: void | string = ''): void {
        const errorMessage = message ? message : 'test';

        // @ts-ignore
        window.toastr.error(errorMessage);
    }
}

const calendar: Calendar = new Calendar();

$(document).on('click', '.add-session', (): void => {
    calendar.addSessions();
});

$(document).on('change', '.form__update-event select[name="production_id"]', (event: JQuery.ChangeEvent): void => {
    event.preventDefault();
    const value: string | string[] | number = $(event.currentTarget).val();
    calendar.getProductionDetails(value);
});

$(document).on(
    'change dp.change',
    '.form__update-event input[name="start_date"]',
    (event: JQuery.ChangeEvent): void => {
        event.preventDefault();
        const value: string | string[] | number = $(event.currentTarget)
            .parents('form')
            .find('select[name="production_id"]')
            .val();
        calendar.getProductionDetails(value);
    }
);

$(document).on('change', '.form__update-event input[name="buffer_time"]', (): void => {
    calendar.updateEndTime();
});

$(document).on('change', '.form__update-event input[name="ticket_system"]', (event: JQuery.ChangeEvent): void => {
    event.preventDefault();
    calendar.checkTicketSystem();
});

$(document).on('change', '.js-event-type-select', (event: JQuery.ChangeEvent): void => {
    event.preventDefault();
    calendar.handleEventTypeChange();
});

$(document).on('change', '.js-visibility-select', (event: JQuery.ChangeEvent): void => {
    event.preventDefault();
    calendar.handleVisibilityChange();
});

$(document).on('click', '.copy-link-btn', (event: JQuery.ClickEvent): void => {
    event.preventDefault();
    const button = $(event.currentTarget);
    const input = button.siblings('.screening-link-input');

    if (input.length && input.val()) {
        // Select the text in the input
        (input[0] as HTMLInputElement).select();

        // Try to copy to clipboard
        try {
            document.execCommand('copy');

            // Show subtle feedback with icon change
            const originalHtml = button.html();
            const originalColor = button.css('color');
            button.html('<i class="voyager-check"></i>');
            button.css('color', '#5cb85c'); // Green for success

            // Reset icon after 1.5 seconds
            setTimeout(() => {
                button.html(originalHtml);
                button.css('color', originalColor);
            }, 1500);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    }
});

// Toggle the radio belonging to the clicked row directly, scoped to that row, instead of relying
// on the native <label for> association. The styled input is visibility:hidden, so the label is
// normally the only way to select it; if anything breaks that association (duplicate ids across
// modals, a DOM-mutating extension, page translation), clicks would otherwise silently fail.
$(document).on('click', 'ul.radio li', (event: JQuery.ClickEvent): void => {
    const input: JQuery = $(event.currentTarget).find('input[type="radio"]');
    if (input.length && !input.prop('checked')) {
        input.prop('checked', true).trigger('change');
    }
});

$(document).on('change', 'input[type="radio"][name="status"]', (event: JQuery.ChangeEvent): void => {
    calendar.toggleAutoPublishNotification();
});

$(document).on('change', 'input[type="radio"][name="public"]', (event: JQuery.ChangeEvent): void => {
    calendar.toggleAutoPublishNotification();
});

$(document).on('click', '.duplicate-sessions__times', (event: JQuery.ClickEvent): void => {
    event.preventDefault();
    event.currentTarget.parentElement.remove();
    calendar.hasDuplicates();
});

$(document).on('submit', '.form__update-event', (event: JQuery.SubmitEvent): void => {
    event.preventDefault();
    const formButtons = $(event.currentTarget).find('.event-modal-actions').find('.btn');

    formButtons.addClass('disabled');

    onFormSubmit($(event.currentTarget))
        .then(calendar.addOrUpdateEvent)
        .then(calendar.closeModal)
        .catch((): void => {
            // The submit failed; onFormSubmit has already surfaced the reason to the user. Swallow the
            // rejection here so the buttons are re-enabled (.always) and the modal stays open to retry.
        })
        .always(() => {
            formButtons.removeClass('disabled');
        });
});

$(document).on('shown.bs.modal', '.modal', (event: JQuery.TriggeredEvent): void => {
    const modal = $(event.target);

    // Only process if this is an event modal
    if (!modal.find('.form__update-event').length) {
        return;
    }

    // Initialize form fields first (this will initialize all select2 elements)
    initFormFields(modal);

    // Then re-initialize event type select with specific options
    const eventTypeSelect = modal.find('.js-event-type-select');

    // Destroy existing select2 if already initialized
    if (eventTypeSelect.hasClass('select2-hidden-accessible')) {
        eventTypeSelect.select2('destroy');
    }

    // Initialize with no search box
    eventTypeSelect.select2({
        minimumResultsForSearch: Infinity, // This is more reliable than -1
        width: '100%',
    });

    // Initialize visibility select
    const visibilitySelect = modal.find('.js-visibility-select');

    // Destroy existing select2 if already initialized
    if (visibilitySelect.hasClass('select2-hidden-accessible')) {
        visibilitySelect.select2('destroy');
    }

    // Initialize with no search box
    visibilitySelect.select2({
        minimumResultsForSearch: Infinity,
        width: '100%',
    });

    // Store the initial event type for change detection
    const currentEventType = eventTypeSelect.val();
    modal.data('previousEventType', currentEventType);

    const productionSelect: JQuery = modal.find('select[name="production_id"]');

    if (!productionSelect.val()) {
        productionSelect.select2('open');
    }

    // Check initial visibility state for hidden link display
    calendar.handleVisibilityChange();

    // Handle event type field visibility immediately
    calendar.handleEventTypeChange();
});

$(document).on('hidden.bs.modal', (event: ModalEventHandler<Document>): void => {
    const modal = $(event.target);
    const modalId = modal.attr('id');
    const isEventModal = modalId ? modalId.includes('event_') : false;

    if (isEventModal) {
        modal.remove();
    }
});

$(document).on('click', '.js-delete-calendar-event', (event: JQuery.ClickEvent): void => {
    calendar.removeEvent($(event.target).data('event'), $(event.target).data('confirm'), $(event.target).data('url'));
});

$(document).on('click', '.js-confirm-publish-submit', (event: JQuery.ClickEvent): void => {
    calendar.confirmPublishEvents();
});

$(document).on('click', '.js-confirm-publish-cancel', (event: JQuery.ClickEvent): void => {
    calendar.cancelConfirmPublishEvents();
});

$(document).on('hidden.bs.modal', (event: ModalEventHandler<Document>): void => {
    const modal = $(event.target);
    const isConfirmModal = modal.attr('id').includes('confirm_publish_events');

    if (isConfirmModal) {
        calendar.cancelConfirmPublishEvents();
    }
});

export default calendar;
